import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Package, AlertCircle, AlertTriangle, TrendingDown,
  Leaf, MoreVertical, Pencil, ArrowUpRight, Activity, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useInventory, useDeleteInventoryItem } from '@/hooks/useInventory';
import {
  INVENTORY_KIND_LABELS,
  type InventoryItem, type InventoryKind,
  isLowStock, isOutOfStock,
} from '@/types/inventory';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { KindBadge, KindIcon } from '@/components/inventory/KindBadge';
import { ItemFormModal } from '@/components/inventory/ItemFormModal';
import { formatCurrency, formatNumber } from '@/lib/utils';

export function InventoryListPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'inventory', 'write') : false;

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'' | InventoryKind>('');
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const { data, isLoading, error } = useInventory();
  const deleteItem = useDeleteInventoryItem();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [deleting, setDeleting] = useState<InventoryItem | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((i) => {
      if (!i.active) return false;
      if (kind && i.kind !== kind) return false;
      if (lowStockOnly && !isLowStock(i) && !isOutOfStock(i)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${i.sku} ${i.name} ${i.description ?? ''} ${i.veneerSpecies ?? ''} ${i.tags?.join(' ') ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, kind, lowStockOnly]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, species: 0, lowStock: 0, totalValue: 0 };
    const active = data.filter((i) => i.active);
    return {
      total: active.length,
      species: new Set(active.filter((i) => i.kind === 'veneer' && i.veneerSpecies).map((i) => i.veneerSpecies)).size,
      lowStock: active.filter((i) => isLowStock(i) || isOutOfStock(i)).length,
      totalValue: active.reduce((sum, i) => sum + (i.valueAtCost ?? 0), 0),
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load inventory"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await deleteItem.mutateAsync(deleting.id);
      toast.success(`Deactivated ${deleting.sku}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-8">
      <SectionHeader
        kicker={`Stock · ${data?.length ?? 0} SKUs`}
        title="Inventory & Materials."
        description="Veneers, substrates, doors, panels, finish, hardware. Multi-location tracking with FSC® chain of custody."
        action={canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Item
          </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Active SKUs"    value={formatNumber(stats.total)} icon={Package} />
        <Stat label="Species"        value={formatNumber(stats.species)} icon={Leaf} hint="of 181" />
        <Stat label="Low / Out"      value={formatNumber(stats.lowStock)} icon={AlertTriangle}
              alert={stats.lowStock > 0} />
        <Stat label="Stock Value"    value={stats.totalValue ? formatCurrency(stats.totalValue) : '—'}
              icon={TrendingDown} accent />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by SKU, name, species, tag…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as InventoryKind | '')}
            options={[
              { value: '', label: 'All kinds' },
              ...Object.entries(INVENTORY_KIND_LABELS).map(([v, l]) => ({ value: v, label: l })),
            ]}
          />
        </div>
        <button
          onClick={() => setLowStockOnly(!lowStockOnly)}
          className={`
            h-11 px-4 text-xs uppercase tracking-[0.15em] rounded-sm border transition-colors inline-flex items-center gap-2
            ${lowStockOnly
              ? 'bg-warning/10 text-warning border-warning/40'
              : 'bg-bg-subtle text-text-muted border-border hover:border-border-strong'}
          `}
        >
          <AlertTriangle size={13} />
          Low Stock
        </button>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={Package}
            title={search || kind || lowStockOnly ? 'No items match your filters' : 'No inventory yet'}
            description={search || kind || lowStockOnly
              ? 'Try clearing some filters.'
              : 'Add your first SKU — a veneer bundle, a substrate sheet, or a finished door.'}
            action={canWrite && !search && !kind && !lowStockOnly && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
                New Item
              </Button>
            )}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">SKU</th>
                <th className="kicker px-6 py-3 font-normal">Item</th>
                <th className="kicker px-6 py-3 font-normal">Kind</th>
                <th className="kicker px-6 py-3 font-normal text-right">On Hand</th>
                <th className="kicker px-6 py-3 font-normal text-right">Value</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const out = isOutOfStock(item);
                const low = !out && isLowStock(item);
                return (
                  <tr key={item.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 transition-colors group">
                    <td className="px-6 py-4 align-top">
                      <Link to={`/inventory/${item.id}`} className="font-mono text-2xs text-brass-300 hover:text-brass-200">
                        {item.sku}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/inventory/${item.id}`} className="flex items-start gap-3 min-w-0">
                        <div className="shrink-0 h-8 w-8 rounded-sm bg-walnut-700/60 flex items-center justify-center mt-0.5">
                          <KindIcon kind={item.kind} size={13} className="text-brass-300" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-text truncate group-hover:text-brass-300 transition-colors">{item.name}</div>
                          {item.kind === 'veneer' && item.veneerSpecies && (
                            <div className="text-xs text-text-dim truncate">
                              {item.veneerSpecies}{item.flitchNumber ? ` · Flitch ${item.flitchNumber}` : ''}
                            </div>
                          )}
                          {item.kind !== 'veneer' && item.dimensions && (
                            <div className="text-xs text-text-dim truncate">{item.dimensions}{item.thickness ? ` × ${item.thickness}` : ''}</div>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4"><KindBadge kind={item.kind} /></td>
                    <td className="px-6 py-4 text-right tabular text-text whitespace-nowrap">
                      {formatNumber(item.totalOnHand ?? 0)}{' '}
                      <span className="text-text-dim text-xs">{item.unit}</span>
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text-muted">
                      {item.valueAtCost ? formatCurrency(item.valueAtCost) : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      {out ? <Badge tone="danger">Out</Badge> :
                       low ? <Badge tone="warning">Low</Badge> :
                       item.fscCertified ? <Badge tone="success">FSC®</Badge> :
                       <span className="inline-flex items-center gap-2 text-xs text-text-muted">
                         <span className="h-1.5 w-1.5 rounded-full bg-success" /> OK
                       </span>}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      {canWrite && (
                        <>
                          <button
                            onClick={(e) => { e.preventDefault(); setOpenMenu(openMenu === item.id ? null : item.id); }}
                            className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors opacity-0 group-hover:opacity-100"
                            aria-label="Actions"
                          >
                            <MoreVertical size={15} />
                          </button>
                          {openMenu === item.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                              <div className="absolute right-4 top-12 z-20 w-44 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                                <Link to={`/inventory/${item.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover">
                                  <ArrowUpRight size={13} /> Open
                                </Link>
                                <Link to={`/inventory/${item.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover">
                                  <Activity size={13} /> Record Movement
                                </Link>
                                <button onClick={() => { setEditing(item); setOpenMenu(null); }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                                  <Pencil size={13} /> Edit
                                </button>
                                <div className="my-1 border-t border-border" />
                                <button onClick={() => { setDeleting(item); setOpenMenu(null); }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                                  <Trash2 size={13} /> Deactivate
                                </button>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <ItemFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ItemFormModal open={!!editing} onClose={() => setEditing(null)} item={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        loading={deleteItem.isPending}
        destructive
        title={`Deactivate ${deleting?.sku}?`}
        confirmLabel="Deactivate"
        description={
          <>
            This marks the SKU as inactive. Stock history and movements are preserved.
            To fully delete an item with history, contact an owner.
          </>
        }
      />
    </div>
  );
}

function Stat({
  label, value, icon: Icon, accent, alert, hint,
}: {
  label: string; value: string; icon: typeof Package;
  accent?: boolean; alert?: boolean; hint?: string;
}) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="kicker">{label}</div>
        <Icon size={12} className={alert ? 'text-warning' : 'text-text-dim'} />
      </div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : alert ? 'text-warning' : 'text-text'}`}>
        {value}
      </div>
      {hint && <div className="text-2xs text-text-dim mt-1">{hint}</div>}
    </div>
  );
}
